/**
 * Calendar days in a named zone — for fields that are DAYS, not moments: due
 * dates, challenge start and end (locale/time-zone audit, 2026-09-26).
 *
 * The API stores such a day as the first or last instant of that day in the
 * WORKSPACE zone (apps/api/src/common/time/calendar-input.ts). Reading one back
 * with the browser's zone moves it: the end of 30 Sep in Dubai is 1 Oct in
 * India, so a learner or admin east of the workspace saw the next day. These
 * helpers read the day in the zone it was written in, the way the API reads it,
 * and format a day without letting any zone shift it again.
 *
 * Send days to the API as `cellToDay(cell)` ('YYYY-MM-DD', lib/time/calendar-cell).
 */
import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { dayToCell } from './calendar-cell'
import { parseApiDate } from './parse-api-date'

const DAY_MS = 24 * 60 * 60 * 1000
const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

function isRealDay(value: string): boolean {
  const m = DAY_ONLY.exec(value)
  if (!m) return false
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.toISOString().slice(0, 10) === value
}

/**
 * The calendar day ('YYYY-MM-DD') a stored date stands for in `tz`, or null.
 *
 * Same reading as the API: a bare day is itself; an instant at local midnight
 * in `tz` (a stored start, an old picker value) is the day it begins; an
 * instant at UTC midnight (old rows written with `new Date('YYYY-MM-DD')`) is
 * the UTC date it names; any other instant is its day in `tz` — which for a
 * due date stored as the end of a day is that day.
 *
 * One more old shape, for display only: the #53 fix stored a date-only due date
 * as 23:59:59.999Z of that UTC date, which read as the NEXT day east of UTC. No
 * zone with a non-zero offset ends a day at that instant, so it is read as the
 * UTC date it ends. (The API keeps such an instant as given when it is written.)
 */
export function calendarDayOf(
  value: string | Date | null | undefined,
  tz: string,
): string | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'string') {
    const s = value.trim()
    if (DAY_ONLY.test(s)) return isRealDay(s) ? s : null
  }
  // An API value: a zone-less timestamp is UTC, never the viewer's local time.
  const d = parseApiDate(value)
  if (!d) return null
  try {
    if (formatInTimeZone(d, tz, 'HH:mm:ss.SSS') === '00:00:00.000') {
      return formatInTimeZone(d, tz, 'yyyy-MM-dd')
    }
    if (d.getTime() % DAY_MS === 0) return d.toISOString().slice(0, 10)
    if ((d.getTime() + 1) % DAY_MS === 0) return d.toISOString().slice(0, 10)
    return formatInTimeZone(d, tz, 'yyyy-MM-dd')
  } catch {
    // An unusable zone must not throw into a render.
    return null
  }
}

/** Today's date in `tz` — "due today or later" is the workspace's today. */
export function todayInZone(tz: string, now: Date = new Date()): string {
  try {
    return formatInTimeZone(now, tz, 'yyyy-MM-dd')
  } catch {
    return format(now, 'yyyy-MM-dd')
  }
}

/** `day` plus `n` calendar days (negative goes back). Pure date arithmetic, no zone. */
export function addDaysToDay(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

/**
 * Whole calendar days from `from` to `to` (both 'YYYY-MM-DD'), negative when
 * `to` is earlier; null when either is not a real day. Both days are already
 * decided, so this is date arithmetic with no zone: a 23- or 25-hour day still
 * counts as one. A countdown is daysBetweenDays(todayInZone(tz), calendarDayOf(due, tz)).
 */
export function daysBetweenDays(from: string, to: string): number | null {
  if (!isRealDay(from) || !isRealDay(to)) return null
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS)
}

/**
 * The react-day-picker cell for the day a stored date stands for in `tz`,
 * optionally `plusDays` later — e.g. an extend dialog's "current due date + 7".
 */
export function dayCellInZone(
  value: string | Date | null | undefined,
  tz: string,
  plusDays = 0,
): Date | undefined {
  const day = calendarDayOf(value, tz)
  return day ? dayToCell(addDaysToDay(day, plusDays)) : undefined
}

/** A day formatted with a date-fns pattern; the day is already decided, so no zone applies. */
export function formatCalendarDay(day: string | null | undefined, pattern = 'PPP'): string {
  if (!day) return ''
  const cell = dayToCell(day)
  return cell ? format(cell, pattern) : ''
}

/**
 * True when a stored due date is the END of its day in `tz` (or a bare day,
 * due by its end) — the only case where "due by the end of X" is true. Rows
 * written before 2026-09-26 hold the old picker's local midnight, the START of
 * X, and fall due as X begins until the A03 repair rewrites them.
 */
export function isEndOfDayIn(value: string | Date | null | undefined, tz: string): boolean {
  if (typeof value === 'string' && DAY_ONLY.test(value.trim())) return isRealDay(value.trim())
  const d = parseApiDate(value)
  if (!d) return false
  try {
    return formatInTimeZone(d, tz, 'HH:mm:ss.SSS') === '23:59:59.999'
  } catch {
    return false
  }
}

/** A stored moment stated in `tz` ("00:00 on September 28th, 2026"); '' when unusable. */
export function formatMomentIn(
  value: string | Date | null | undefined,
  tz: string,
  pattern = "HH:mm 'on' PPP",
): string {
  const d = parseApiDate(value)
  if (!d) return ''
  try {
    return formatInTimeZone(d, tz, pattern)
  } catch {
    return ''
  }
}
